import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import { 
    Upload, 
    FileText, 
    Download, 
    Copy, 
    Trash2, 
    Search, 
    RefreshCw, 
    Check, 
    Folder, 
    ExternalLink,
    Edit2,
    Tag,
    X,
    Eye,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminResources = () => {
    const { t, i18n } = useTranslation();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const isKa = i18n.language === 'ka';

    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [uploading, setUploading] = useState(false);

    // Lightbox & PDF Modal State
    const [activePdfModal, setActivePdfModal] = useState(null);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    // Upload Modal State
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadForm, setUploadForm] = useState({
        title: '',
        category: 'brandbook',
        file: null
    });

    // Category Edit Modal State
    const [editResource, setEditResource] = useState(null);
    const [editForm, setEditForm] = useState({
        title: '',
        category: 'brandbook'
    });
    const [savingEdit, setSavingEdit] = useState(false);

    const [copiedId, setCopiedId] = useState(null);

    const openLightbox = (imagesList, startIndex = 0) => {
        setLightboxImages(imagesList);
        setLightboxIndex(startIndex);
        setIsLightboxOpen(true);
    };

    const closeLightbox = () => setIsLightboxOpen(false);

    const prevLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === 0 ? lightboxImages.length - 1 : prev - 1));
    }, [lightboxImages.length]);

    const nextLightboxImage = useCallback(() => {
        setLightboxIndex(prev => (prev === lightboxImages.length - 1 ? 0 : prev + 1));
    }, [lightboxImages.length]);

    useEffect(() => {
        if (!isLightboxOpen && !activePdfModal) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isLightboxOpen) closeLightbox();
                if (activePdfModal) setActivePdfModal(null);
            }
            if (isLightboxOpen) {
                if (e.key === 'ArrowLeft') prevLightboxImage();
                if (e.key === 'ArrowRight') nextLightboxImage();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLightboxOpen, activePdfModal, prevLightboxImage, nextLightboxImage]);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchResources = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}resources.php`);
            const data = await res.json();
            setResources(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching resources:', err);
            toast.error(t('common.toastLoadError') || 'რესურსების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user) fetchResources();
    }, [user, fetchResources]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const f = e.target.files[0];
            setUploadForm(prev => ({
                ...prev,
                file: f,
                title: prev.title || f.name.replace(/\.[^/.]+$/, '')
            }));
        }
    };

    const handleUploadSubmit = async (e) => {
        e.preventDefault();
        if (!uploadForm.file) {
            toast.error(isKa ? 'გთხოვთ აირჩიოთ ფაილი' : 'Please select a file');
            return;
        }

        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', uploadForm.file);
            fd.append('title', uploadForm.title);
            fd.append('category', uploadForm.category);

            const res = await fetchWithSecurity(`${API_URL}resources.php`, {
                method: 'POST',
                body: fd
            });
            const result = await res.json();
            if (result.success) {
                toast.success(isKa ? 'რესურსი წარმატებით აიტვირთა!' : 'Resource uploaded successfully!');
                setUploadModalOpen(false);
                setUploadForm({ title: '', category: 'brandbook', file: null });
                fetchResources();
            } else {
                toast.error(result.error || (isKa ? 'ატვირთვა ვერ მოხერხდა' : 'Upload failed'));
            }
        } catch (err) {
            toast.error(isKa ? 'ატვირთვა ვერ მოხერხდა' : 'Upload failed');
        }
        setUploading(false);
    };

    // Category Change Handler
    const handleUpdateCategorySubmit = async (e) => {
        e.preventDefault();
        if (!editResource) return;

        setSavingEdit(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}resources.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: editResource.id,
                    category: editForm.category,
                    title: editForm.title
                })
            });
            const result = await res.json();
            if (result.success) {
                toast.success(isKa ? 'კატეგორია წარმატებით შეიცვალა!' : 'Category updated successfully!');
                setResources(prev => prev.map(r => 
                    r.id === editResource.id 
                        ? { ...r, category: editForm.category, title: editForm.title } 
                        : r
                ));
                setEditResource(null);
            } else {
                toast.error(result.error || (isKa ? 'შეცვლა ვერ მოხერხდა' : 'Failed to update'));
            }
        } catch (err) {
            console.error('Category update error:', err);
            toast.error(isKa ? 'შეცდომა კატეგორიის შეცვლისას' : 'Error updating category');
        }
        setSavingEdit(false);
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(isKa ? `დარწმუნებული ხართ, რომ გსურთ წაშალოთ "${title}"?` : `Are you sure you want to delete "${title}"?`)) return;

        try {
            const res = await fetchWithSecurity(`${API_URL}resources.php?id=${id}`, {
                method: 'DELETE'
            });
            const result = await res.json();
            if (result.success) {
                toast.success(isKa ? 'ფაილი წაიშალა' : 'File deleted');
                fetchResources();
            } else {
                toast.error(result.error || (isKa ? 'წაშლა ვერ მოხერხდა' : 'Delete failed'));
            }
        } catch (err) {
            toast.error(isKa ? 'წაშლა ვერ მოხერხდა' : 'Delete failed');
        }
    };

    const handleCopyUrl = (filePath, id) => {
        const fullUrl = filePath.startsWith('http') ? filePath : `${API_URL}${filePath}`;
        navigator.clipboard.writeText(fullUrl);
        setCopiedId(id);
        toast.success(isKa ? 'ბმული დაკოპირდა!' : 'Link copied to clipboard!');
        setTimeout(() => setCopiedId(null), 2000);
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getCategoryBadge = (cat) => {
        switch (cat) {
            case 'brandbook': return { label: isKa ? '📘 ბრენდბუქი' : '📘 Brandbook', bg: '#ede9fe', color: '#6d28d9' };
            case 'logo': return { label: isKa ? '🎨 ლოგო' : '🎨 Logo', bg: '#e0f2fe', color: '#0369a1' };
            case 'image': return { label: isKa ? '🖼️ სურათი' : '🖼️ Image', bg: '#fef3c7', color: '#b45309' };
            case 'document': return { label: isKa ? '📄 დოკუმენტი' : '📄 Document', bg: '#dcfce7', color: '#15803d' };
            default: return { label: cat, bg: '#f1f5f9', color: '#475569' };
        }
    };

    const categoriesList = [
        { key: 'all', label: isKa ? 'ყველა რესურსი' : 'All Resources' },
        { key: 'brandbook', label: isKa ? '📘 ბრენდბუქები (PDF)' : '📘 Brandbooks (PDF)' },
        { key: 'logo', label: isKa ? '🎨 ლოგოები (SVG/PNG)' : '🎨 Logos (SVG/PNG)' },
        { key: 'image', label: isKa ? '🖼️ სურათები & მედია' : '🖼️ Images & Media' },
        { key: 'document', label: isKa ? '📄 დოკუმენტები' : '📄 Documents' }
    ];

    const categoryOptions = [
        { value: 'brandbook', label: isKa ? '📘 ბრენდბუქი (PDF)' : '📘 Brandbook (PDF)', color: '#6d28d9' },
        { value: 'logo', label: isKa ? '🎨 ლოგო (SVG/PNG)' : '🎨 Logo (SVG/PNG)', color: '#0369a1' },
        { value: 'image', label: isKa ? '🖼️ სურათი & გრაფიკა' : '🖼️ Image & Media', color: '#b45309' },
        { value: 'document', label: isKa ? '📄 დოკუმენტი / პრეზენტაცია' : '📄 Document', color: '#15803d' },
    ];

    const filteredResources = resources
        .filter(r => activeCategory === 'all' || r.category === activeCategory)
        .filter(r => !searchQuery.trim() || r.title.toLowerCase().includes(searchQuery.toLowerCase()) || (r.file_name && r.file_name.toLowerCase().includes(searchQuery.toLowerCase())));

    const handleOpenImage = (currentRes) => {
        const isImageFile = (r) => {
            const ext = (r.file_type || r.file_path?.split('.').pop() || '').toLowerCase();
            return ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif', 'bmp', 'ico', 'avif'].includes(ext);
        };

        const imageList = filteredResources
            .filter(isImageFile)
            .map(r => ({
                id: r.id,
                url: r.file_path.startsWith('http') ? r.file_path : `${API_URL}${r.file_path}`,
                title: r.title,
                fileName: r.file_name,
                fileSize: r.file_size,
                date: r.created_at
            }));

        const startIndex = Math.max(0, imageList.findIndex(img => img.id === currentRes.id));
        openLightbox(imageList, startIndex);
    };

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={isKa ? 'ბრენდის რესურსები & ფაილები' : 'Brand Resources & Media'}>
            <style>{`
                .resource-thumb-container:hover .thumb-hover-overlay {
                    opacity: 1 !important;
                }
            `}</style>
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1">{isKa ? 'ბრენდის რესურსების მართვა' : 'Brand Resources & Downloads'}</h4>
                    <p className="text-muted small mb-0">{isKa ? 'ატვირთეთ და გააზიარეთ PDF ბრენდბუქები, ლოგოები, პრეზენტაციები და გრაფიკული აქტივები' : 'Upload and share PDF brandbooks, official logos, presentations and assets'}</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="shadcn-btn shadcn-btn-secondary" onClick={fetchResources}>
                        <RefreshCw size={15} /> {isKa ? 'განახლება' : 'Refresh'}
                    </button>
                    <button className="shadcn-btn shadcn-btn-primary" onClick={() => setUploadModalOpen(true)}>
                        <Upload size={15} /> {isKa ? 'ახალი რესურსის ატვირთვა' : 'Upload Resource'}
                    </button>
                </div>
            </div>

            {/* Filter Pills & Search */}
            <div className="shadcn-card p-3 mb-4">
                <div className="row g-3 align-items-center">
                    <div className="col-lg-8">
                        <div className="d-flex gap-2 flex-wrap">
                            {categoriesList.map(c => {
                                const count = c.key === 'all' ? resources.length : resources.filter(r => r.category === c.key).length;
                                const isActive = activeCategory === c.key;
                                return (
                                    <button
                                        key={c.key}
                                        className={`shadcn-btn shadcn-btn-sm ${isActive ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                        onClick={() => setActiveCategory(c.key)}
                                    >
                                        {c.label} ({count})
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="position-relative">
                            <Search size={16} className="position-absolute text-muted" style={{ left: '12px', top: '10px' }} />
                            <input
                                type="text"
                                className="shadcn-input ps-5"
                                placeholder={isKa ? 'ძებნა სათაურით ან ფაილით...' : 'Search resources...'}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Resources Grid */}
            {loading ? (
                <div className="text-center py-5 text-muted">იტვირთება...</div>
            ) : filteredResources.length === 0 ? (
                <div className="shadcn-card text-center py-5">
                    <Folder size={48} className="text-muted mb-3 opacity-50" />
                    <h5 className="fw-bold">{isKa ? 'რესურსები არ მოიძებნა' : 'No resources found'}</h5>
                    <p className="text-muted small">{isKa ? 'ატვირთეთ ახალი ბრენდბუქი ან ლოგო ზედა ღილაკით' : 'Upload a new brandbook or logo using the button above'}</p>
                </div>
            ) : (
                <div className="row g-4">
                    {filteredResources.map(res => {
                        const badge = getCategoryBadge(res.category);
                        const fileUrl = res.file_path.startsWith('http') ? res.file_path : `${API_URL}${res.file_path}`;
                        const ext = (res.file_type || res.file_path?.split('.').pop() || '').toLowerCase();
                        const isImg = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif', 'bmp', 'ico', 'avif'].includes(ext);
                        const isPdf = ext === 'pdf';

                        return (
                            <div className="col-md-6 col-lg-4 col-xl-3" key={res.id}>
                                <div className="shadcn-card h-100 d-flex flex-column justify-content-between p-3 position-relative hover-shadow" style={{ transition: 'all 0.2s' }}>
                                    <div>
                                        {/* Thumbnail or File Icon */}
                                        <div 
                                            className="d-flex align-items-center justify-content-center rounded-3 mb-3 position-relative overflow-hidden resource-thumb-container" 
                                            style={{ 
                                                height: '140px', 
                                                background: isImg ? '#0f172a' : '#f8fafc',
                                                border: '1px solid #e2e8f0',
                                                cursor: (isImg || isPdf) ? 'pointer' : 'default'
                                            }}
                                            onClick={() => {
                                                if (isImg) handleOpenImage(res);
                                                else if (isPdf) setActivePdfModal(res);
                                            }}
                                            title={isPdf ? (isKa ? 'დააწკაპუნეთ PDF-ის გასახსნელად' : 'Click to preview PDF') : isImg ? (isKa ? 'დააწკაპუნეთ გასადიდებლად' : 'Click to view image') : ''}
                                        >
                                            {isImg ? (
                                                <img 
                                                    src={fileUrl} 
                                                    alt={res.title} 
                                                    style={{ maxHeight: '100px', maxWidth: '85%', objectFit: 'contain' }} 
                                                />
                                            ) : (
                                                <div className="text-center">
                                                    <FileText size={48} className={isPdf ? 'text-danger opacity-85 mb-1' : 'text-primary opacity-75 mb-1'} />
                                                    <div className={`font-monospace text-uppercase small fw-bold ${isPdf ? 'text-danger' : 'text-muted'}`}>{res.file_type || ext}</div>
                                                </div>
                                            )}

                                            {/* Hover Preview Overlay */}
                                            {(isImg || isPdf) && (
                                                <div 
                                                    className="thumb-hover-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                    style={{ 
                                                        background: 'rgba(15, 23, 42, 0.45)', 
                                                        opacity: 0, 
                                                        transition: 'opacity 0.2s',
                                                        pointerEvents: 'none'
                                                    }}
                                                >
                                                    <span className={`badge ${isPdf ? 'bg-danger text-white' : 'bg-white text-dark'} shadow-sm px-2 py-1 d-flex align-items-center gap-1 font-semibold`}>
                                                        <Eye size={13} /> {isPdf ? (isKa ? 'PDF ნახვა' : 'View PDF') : (isKa ? 'ნახვა' : 'Preview')}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            {/* Clickable Category Badge to Edit */}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditResource(res);
                                                    setEditForm({ title: res.title, category: res.category });
                                                }}
                                                className="position-absolute top-0 end-0 m-2 px-2 py-1 rounded-pill smallest fw-bold border-0 cursor-pointer d-flex align-items-center gap-1 shadow-sm"
                                                style={{ background: badge.bg, color: badge.color, cursor: 'pointer', zIndex: 2 }}
                                                title={isKa ? 'კატეგორიის შეცვლა' : 'Change category'}
                                            >
                                                <span>{badge.label}</span>
                                                <Edit2 size={10} />
                                            </button>
                                        </div>

                                        <h6 className="fw-bold text-dark text-truncate mb-1" title={res.title}>
                                            {res.title}
                                        </h6>
                                        <div className="d-flex justify-content-between align-items-center text-muted smallest mb-3">
                                            <span>{formatBytes(res.file_size)}</span>
                                            <span>{new Date(res.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="d-flex gap-1 pt-2 border-top">
                                        {(isImg || isPdf) && (
                                            <button 
                                                type="button"
                                                className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" 
                                                onClick={() => {
                                                    if (isImg) handleOpenImage(res);
                                                    else if (isPdf) setActivePdfModal(res);
                                                }}
                                                title={isPdf ? (isKa ? 'PDF-ის ნახვა' : 'View PDF') : (isKa ? 'ნახვა' : 'Preview')}
                                            >
                                                <Eye size={13} />
                                            </button>
                                        )}
                                        <a 
                                            href={fileUrl} 
                                            target="_blank" 
                                            rel="noreferrer" 
                                            download 
                                            className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm flex-grow-1 text-decoration-none text-center"
                                            title={isKa ? 'ჩამოტვირთვა' : 'Download'}
                                        >
                                            <Download size={13} /> {isKa ? 'ჩამოტვირთვა' : 'Download'}
                                        </a>
                                        <button 
                                            type="button"
                                            className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" 
                                            onClick={() => {
                                                setEditResource(res);
                                                setEditForm({ title: res.title, category: res.category });
                                            }}
                                            title={isKa ? 'კატეგორიის შეცვლა' : 'Change category'}
                                        >
                                            <Tag size={13} />
                                        </button>
                                        <button 
                                            type="button"
                                            className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" 
                                            onClick={() => handleCopyUrl(res.file_path, res.id)}
                                            title={isKa ? 'ბმულის კოპირება' : 'Copy link'}
                                        >
                                            {copiedId === res.id ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                                        </button>
                                        <button 
                                            type="button"
                                            className="shadcn-btn shadcn-btn-danger shadcn-btn-sm" 
                                            onClick={() => handleDelete(res.id, res.title)}
                                            title={isKa ? 'წაშლა' : 'Delete'}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── CHANGE CATEGORY MODAL ── */}
            {editResource && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px' }}>
                            <div className="modal-header border-bottom p-3">
                                <h5 className="modal-title fw-bold fs-6 d-flex align-items-center gap-2">
                                    <Tag size={18} className="text-primary" />
                                    {isKa ? 'კატეგორიის შეცვლა' : 'Change Resource Category'}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setEditResource(null)} />
                            </div>
                            <form onSubmit={handleUpdateCategorySubmit}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">{isKa ? 'რესურსის სათაური' : 'Resource Title'}</label>
                                        <input
                                            type="text"
                                            className="shadcn-input"
                                            value={editForm.title}
                                            onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold mb-2">
                                            {isKa ? 'აირჩიეთ ახალი კატეგორია' : 'Select New Category'}
                                        </label>
                                        <div className="d-flex flex-column gap-2">
                                            {categoryOptions.map(opt => (
                                                <label
                                                    key={opt.value}
                                                    className="d-flex align-items-center gap-3 p-3 rounded border cursor-pointer"
                                                    style={{
                                                        background: editForm.category === opt.value ? `${opt.color}10` : '#f8fafc',
                                                        borderColor: editForm.category === opt.value ? opt.color : '#e2e8f0',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="editCategory"
                                                        value={opt.value}
                                                        checked={editForm.category === opt.value}
                                                        onChange={() => setEditForm({ ...editForm, category: opt.value })}
                                                        className="form-check-input mt-0"
                                                    />
                                                    <span className="fw-semibold small" style={{ color: editForm.category === opt.value ? opt.color : '#1e293b' }}>
                                                        {opt.label}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="p-2 bg-light rounded text-muted smallest d-flex align-items-center gap-2">
                                        <FileText size={14} />
                                        <span>ფაილი: {editResource.file_name} ({formatBytes(editResource.file_size)})</span>
                                    </div>
                                </div>
                                <div className="modal-footer border-top p-3">
                                    <button 
                                        type="button" 
                                        className="shadcn-btn shadcn-btn-secondary" 
                                        onClick={() => setEditResource(null)}
                                    >
                                        {isKa ? 'გაუქმება' : 'Cancel'}
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="shadcn-btn shadcn-btn-primary" 
                                        disabled={savingEdit}
                                    >
                                        {savingEdit ? (isKa ? 'ინახება...' : 'Saving...') : (isKa ? 'შენახვა' : 'Save Changes')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── UPLOAD MODAL ── */}
            {uploadModalOpen && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px' }}>
                            <div className="modal-header border-bottom p-3">
                                <h5 className="modal-title fw-bold fs-6 d-flex align-items-center gap-2">
                                    <Upload size={18} className="text-primary" />
                                    {isKa ? 'ახალი რესურსის ატვირთვა' : 'Upload New Resource'}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setUploadModalOpen(false)} />
                            </div>
                            <form onSubmit={handleUploadSubmit}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">{isKa ? 'ფაილის შერჩევა' : 'Choose File'} *</label>
                                        <input 
                                            type="file" 
                                            className="form-control form-control-sm" 
                                            onChange={handleFileChange} 
                                            required 
                                        />
                                        <small className="text-muted smallest">
                                            {isKa ? 'მხარდაჭერილია: PDF, PNG, JPG, SVG, WEBP, ZIP, DOCX' : 'Supported: PDF, PNG, JPG, SVG, WEBP, ZIP, DOCX'}
                                        </small>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">{isKa ? 'სათაური / დასახელება' : 'Title'} *</label>
                                        <input 
                                            type="text" 
                                            className="shadcn-input" 
                                            value={uploadForm.title} 
                                            onChange={e => setUploadForm(prev => ({ ...prev, title: e.target.value }))} 
                                            placeholder={isKa ? 'მაგ: Orbitronix Brandbook 2026' : 'e.g. Orbitronix Brandbook 2026'} 
                                            required 
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">{isKa ? 'კატეგორია' : 'Category'} *</label>
                                        <select 
                                            className="shadcn-input"
                                            value={uploadForm.category}
                                            onChange={e => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                                        >
                                            <option value="brandbook">{isKa ? '📘 ბრენდბუქი (PDF)' : '📘 Brandbook (PDF)'}</option>
                                            <option value="logo">{isKa ? '🎨 ლოგო (SVG / PNG)' : '🎨 Logo (SVG / PNG)'}</option>
                                            <option value="image">{isKa ? '🖼️ სურათი & მედია' : '🖼️ Image & Media'}</option>
                                            <option value="document">{isKa ? '📄 დოკუმენტი / პრეზენტაცია' : '📄 Document / Presentation'}</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="modal-footer border-top p-3">
                                    <button type="button" className="shadcn-btn shadcn-btn-secondary" onClick={() => setUploadModalOpen(false)}>
                                        {isKa ? 'გაუქმება' : 'Cancel'}
                                    </button>
                                    <button type="submit" className="shadcn-btn shadcn-btn-primary" disabled={uploading}>
                                        {uploading ? (isKa ? 'იტვირთება...' : 'Uploading...') : (isKa ? 'ატვირთვა' : 'Upload')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ── PDF PREVIEW MODAL ── */}
            {activePdfModal && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    onClick={() => setActivePdfModal(null)}
                    style={{ background: 'rgba(5, 8, 22, 0.88)', backdropFilter: 'blur(8px)', zIndex: 1060 }}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered modal-xl" 
                        style={{ maxWidth: '1100px', width: '95%' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-content border-0 shadow-lg text-white" style={{ borderRadius: '16px', overflow: 'hidden', background: '#0f172a' }}>
                            <div className="modal-header border-bottom border-secondary border-opacity-25 p-3 px-4 d-flex justify-content-between align-items-center" style={{ background: '#0b1120' }}>
                                <div className="d-flex align-items-center gap-3 text-truncate me-3">
                                    <div className="p-2 rounded bg-danger bg-opacity-10 text-danger flex-shrink-0 d-flex align-items-center justify-content-center">
                                        <FileText size={22} />
                                    </div>
                                    <div className="text-truncate">
                                        <h6 className="modal-title fw-bold text-white mb-0 text-truncate">{activePdfModal.title}</h6>
                                        <span className="smallest text-white-50 font-monospace">
                                            {activePdfModal.file_name} • {formatBytes(activePdfModal.file_size)}
                                        </span>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <a 
                                        href={activePdfModal.file_path.startsWith('http') ? activePdfModal.file_path : `${API_URL}${activePdfModal.file_path}`} 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                                        title={isKa ? 'ახალ ჩანართში გახსნა' : 'Open in new tab'}
                                    >
                                        <ExternalLink size={14} />
                                        <span className="d-none d-sm-inline">{isKa ? 'გახსნა' : 'Open'}</span>
                                    </a>
                                    <a 
                                        href={activePdfModal.file_path.startsWith('http') ? activePdfModal.file_path : `${API_URL}${activePdfModal.file_path}`} 
                                        download 
                                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                        title={isKa ? 'ჩამოტვირთვა' : 'Download'}
                                    >
                                        <Download size={14} />
                                        <span className="d-none d-sm-inline">{isKa ? 'ჩამოტვირთვა' : 'Download'}</span>
                                    </a>
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-light rounded-circle p-2 ms-2 d-flex align-items-center justify-content-center"
                                        style={{ width: '34px', height: '34px', border: '1px solid rgba(255,255,255,0.2)' }}
                                        onClick={() => setActivePdfModal(null)}
                                        title={isKa ? 'დახურვა' : 'Close'}
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                            <div className="modal-body p-0" style={{ height: '78vh', background: '#1e293b' }}>
                                <iframe 
                                    src={activePdfModal.file_path.startsWith('http') ? activePdfModal.file_path : `${API_URL}${activePdfModal.file_path}`}
                                    title={activePdfModal.title}
                                    width="100%" 
                                    height="100%" 
                                    style={{ border: 'none', display: 'block' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── IMAGE LIGHTBOX WITH PREV/NEXT CONTROLS ── */}
            {isLightboxOpen && lightboxImages.length > 0 && (
                <div 
                    className="orbx-lightbox-overlay" 
                    onClick={closeLightbox}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(5, 8, 22, 0.94)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 999999,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '24px'
                    }}
                >
                    {/* Top Bar */}
                    <div 
                        className="d-flex justify-content-between align-items-center w-100"
                        style={{ maxWidth: '1200px', zIndex: 1000000 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="text-white text-truncate me-3">
                            <span className="fw-bold fs-6 d-block text-white text-truncate">
                                {lightboxImages[lightboxIndex]?.title}
                            </span>
                            <span className="smallest text-white-50 font-monospace">
                                {lightboxImages[lightboxIndex]?.fileName} • {formatBytes(lightboxImages[lightboxIndex]?.fileSize)} • {lightboxImages[lightboxIndex]?.date ? new Date(lightboxImages[lightboxIndex].date).toLocaleDateString() : ''}
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge rounded-pill bg-white bg-opacity-10 text-white px-3 py-2 fw-semibold fs-7 border border-white border-opacity-10">
                                {lightboxIndex + 1} / {lightboxImages.length}
                            </span>
                            <a
                                href={lightboxImages[lightboxIndex]?.url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '40px', height: '40px', border: '1px solid rgba(255,255,255,0.2)' }}
                                title={isKa ? 'ახალ ჩანართში გახსნა' : 'Open in new tab'}
                            >
                                <ExternalLink size={18} />
                            </a>
                            <a
                                href={lightboxImages[lightboxIndex]?.url}
                                download
                                className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '40px', height: '40px', border: '1px solid rgba(255,255,255,0.2)' }}
                                title={isKa ? 'ჩამოტვირთვა' : 'Download'}
                            >
                                <Download size={18} />
                            </a>
                            <button
                                type="button"
                                onClick={closeLightbox}
                                className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center ms-1"
                                style={{ width: '40px', height: '40px', border: '1px solid rgba(255,255,255,0.2)' }}
                                title={isKa ? 'დახურვა' : 'Close'}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Main Image Viewer */}
                    <div 
                        className="position-relative d-flex align-items-center justify-content-center flex-grow-1 w-100 my-3"
                        style={{ minHeight: 0 }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Prev Button */}
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={prevLightboxImage}
                                className="btn position-absolute start-0 top-50 translate-middle-y text-white rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                style={{
                                    zIndex: 1000001,
                                    width: '54px',
                                    height: '54px',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    cursor: 'pointer'
                                }}
                                title={isKa ? 'წინა' : 'Previous'}
                            >
                                <ChevronLeft size={30} />
                            </button>
                        )}

                        <div className="d-flex align-items-center justify-content-center h-100 w-100 p-2">
                            <img
                                src={lightboxImages[lightboxIndex]?.url}
                                alt={lightboxImages[lightboxIndex]?.title || 'Resource Preview'}
                                style={{
                                    maxHeight: '78vh',
                                    maxWidth: '82vw',
                                    objectFit: 'contain',
                                    borderRadius: '12px',
                                    boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)'
                                }}
                            />
                        </div>

                        {/* Next Button */}
                        {lightboxImages.length > 1 && (
                            <button
                                type="button"
                                onClick={nextLightboxImage}
                                className="btn position-absolute end-0 top-50 translate-middle-y text-white rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                                style={{
                                    zIndex: 1000001,
                                    width: '54px',
                                    height: '54px',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    cursor: 'pointer'
                                }}
                                title={isKa ? 'შემდეგი' : 'Next'}
                            >
                                <ChevronRight size={30} />
                            </button>
                        )}
                    </div>

                    {/* Bottom Thumbnails Strip */}
                    {lightboxImages.length > 1 && (
                        <div 
                            className="d-flex gap-2 justify-content-center overflow-auto py-2 px-3 rounded-pill bg-black bg-opacity-40 border border-white border-opacity-10"
                            style={{ maxWidth: '90vw', zIndex: 1000000 }}
                            onClick={e => e.stopPropagation()}
                        >
                            {lightboxImages.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img.url}
                                    alt={`thumb-${idx}`}
                                    onClick={() => setLightboxIndex(idx)}
                                    style={{
                                        width: '46px',
                                        height: '34px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        opacity: idx === lightboxIndex ? 1 : 0.45,
                                        border: idx === lightboxIndex ? '2px solid #6366f1' : '1px solid transparent',
                                        transform: idx === lightboxIndex ? 'scale(1.1)' : 'scale(1)',
                                        transition: 'all 0.2s ease'
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminResources;
